import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Eye, RefreshCw } from 'lucide-react';

const ProblemSection = () => {
  const problems = [
    {
      icon: AlertTriangle,
      title: 'Hidden Data Sales',
      description: 'Apps selling your child\'s location history, browsing habits, and personal information to third parties you\'ve never heard of.',
      color: '#D97706',
    },
    {
      icon: Eye,
      title: 'Rights Waivers',
      description: 'Giving up ownership of family photos, videos, and memories buried in terms you never had time to read.',
      color: '#0D9488',
    },
    {
      icon: RefreshCw,
      title: 'Silent Updates',
      description: 'Policies changing while you sleep, expanding data collection and reducing your family\'s privacy protections.',
      color: '#EF4444',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <section className="py-24 md:py-32 bg-stone-50" id="problem" data-testid="problem-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-[#D97706] font-medium text-sm uppercase tracking-wider">The Problem</span>
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#292524] mt-4"
            data-testid="problem-heading"
          >
            The Fine Print Trap
          </h2>
          <p className="mt-6 text-lg text-[#78716C] max-w-2xl mx-auto">
            The average terms of service is 7,000 words. No parent has time for that. But buried in those words are decisions being made about your family.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              variants={itemVariants}
              className="bg-white rounded-3xl p-8 hover:shadow-lg transition-shadow duration-300"
              data-testid={`problem-card-${index}`}
            >
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: `${problem.color}15` }}
              >
                <problem.icon className="w-7 h-7" style={{ color: problem.color }} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#292524] mb-3">
                {problem.title}
              </h3>
              <p className="text-[#78716C] leading-relaxed">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
